import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Calendar,
  AppointmentForm,
  CalendarView,
  Appointment as CalendarAppointment,
  AppointmentFormData,
  CalendarWorkingSettings,
} from '../../components/Calendar';
import { Button } from '../../components/UI/Button';
import { Card } from '../../components/UI/Card';
import ScheduleApiService from '../../services/scheduleApi';
import { CALENDAR_SETTINGS } from '../../config/calendarSettings';
import { getUserPreferences, subscribeUserPreferences } from '../../services/userPreferences';

interface ScheduleNavigationState {
  openNewAppointment?: boolean;
  clientId?: string;
  clientName?: string;
  duration?: number;
  startTime?: string;
  endTime?: string;
}

const STATUS_CONFIG: Array<{
  value: CalendarAppointment['status'];
  label: string;
  className: string;
}> = [
  { value: 'scheduled', label: 'Planifié', className: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 'confirmed', label: 'Confirmé', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  { value: 'in_progress', label: 'En cours', className: 'bg-amber-100 text-amber-800 border-amber-200' },
  { value: 'completed', label: 'Terminé', className: 'bg-gray-100 text-gray-700 border-gray-200' },
  { value: 'cancelled', label: 'Annulé', className: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'no_show', label: 'Absent', className: 'bg-orange-100 text-orange-700 border-orange-200' },
];

const ALL_STATUS_VALUES = STATUS_CONFIG.map((status) => status.value);

const getCalendarWorkingSettings = (): CalendarWorkingSettings => {
  const preferences = getUserPreferences();

  return {
    workdayStartTime: preferences.workdayStartTime,
    workdayEndTime: preferences.workdayEndTime,
    enableMiddayBreak: preferences.enableMiddayBreak,
    breakStartTime: preferences.breakStartTime,
    breakEndTime: preferences.breakEndTime,
  };
};

const getAgendaDenseMode = (): boolean => {
  return getUserPreferences().agendaDenseMode;
};

const getDefaultVisitDuration = (): number => {
  const preferredDuration = getUserPreferences().defaultVisitDuration;
  return Number.isFinite(preferredDuration) && preferredDuration > 0
    ? Math.round(preferredDuration)
    : CALENDAR_SETTINGS.defaultAppointmentDurationMinutes;
};

const Schedule: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // État du calendrier
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<CalendarView>('week');
  const [appointments, setAppointments] = useState<CalendarAppointment[]>([]);
  const [workingSettings, setWorkingSettings] = useState<CalendarWorkingSettings>(() => getCalendarWorkingSettings());
  const [agendaDenseMode, setAgendaDenseMode] = useState<boolean>(() => getAgendaDenseMode());
  const [defaultVisitDuration, setDefaultVisitDuration] = useState<number>(() => getDefaultVisitDuration());
  const [visibleStatuses, setVisibleStatuses] = useState<Array<CalendarAppointment['status']>>(ALL_STATUS_VALUES);
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // État du formulaire de rendez-vous
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<CalendarAppointment | undefined>();
  const [initialFormData, setInitialFormData] = useState<Partial<AppointmentFormData> | undefined>();
  // Client depuis lequel on a navigué — pour y revenir après création
  const [returnToClientId, setReturnToClientId] = useState<string | null>(null);

  const getErrorMessage = (error: unknown, fallback: string) => {
    if (error instanceof Error && error.message.trim()) {
      return error.message;
    }
    return fallback;
  };

  const periodLabel = useMemo(() => {
    if (currentView === 'day') {
      return format(currentDate, 'EEEE d MMMM yyyy', { locale: fr });
    }

    if (currentView === 'week') {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(start, 'd MMM', { locale: fr })} - ${format(end, 'd MMM yyyy', { locale: fr })}`;
    }

    return format(currentDate, 'MMMM yyyy', { locale: fr });
  }, [currentDate, currentView]);

  const statusCounts = useMemo(() => {
    const counts = Object.fromEntries(ALL_STATUS_VALUES.map((value) => [value, 0])) as Record<
      CalendarAppointment['status'],
      number
    >;

    appointments.forEach((appointment) => {
      counts[appointment.status] += 1;
    });

    return counts;
  }, [appointments]);

  const filteredAppointments = useMemo(
    () => appointments.filter((appointment) => visibleStatuses.includes(appointment.status)),
    [appointments, visibleStatuses]
  );

  const toggleStatusFilter = (status: CalendarAppointment['status']) => {
    setVisibleStatuses((current) => {
      if (current.includes(status)) {
        const next = current.filter((value) => value !== status);
        return next.length > 0 ? next : current;
      }

      return [...current, status];
    });
  };

  const enableAllStatuses = () => {
    setVisibleStatuses(ALL_STATUS_VALUES);
  };

  // Charger les rendez-vous
  const loadAppointments = useCallback(async () => {
    setIsLoading(true);
    try {
      switch (currentView) {
        case 'day': {
          const dayResult = await ScheduleApiService.getDaySchedule(currentDate);
          setAppointments(dayResult.appointments);
          break;
        }
        case 'week': {
          const weekResult = await ScheduleApiService.getWeekSchedule(currentDate);
          setAppointments(weekResult.appointments);
          break;
        }
        case 'month': {
          const monthResult = await ScheduleApiService.getMonthSchedule(currentDate);
          setAppointments(monthResult.appointments);
          break;
        }
        default:
          setAppointments([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des rendez-vous:', error);
      setFeedback({
        type: 'error',
        message: getErrorMessage(error, 'Impossible de charger l\'agenda pour la période sélectionnée.')
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentDate, currentView]);

  // Charger les rendez-vous au changement de date ou de vue
  useEffect(() => {
    void loadAppointments();
  }, [loadAppointments]);

  useEffect(() => {
    const navigationState = location.state as ScheduleNavigationState | null;

    if (!navigationState?.openNewAppointment) {
      return;
    }

    const startTime = navigationState.startTime ? new Date(navigationState.startTime) : new Date();
    const duration = navigationState.duration || defaultVisitDuration;
    const endTime = navigationState.endTime
      ? new Date(navigationState.endTime)
      : new Date(startTime.getTime() + duration * 60 * 1000);

    setCurrentDate(startTime);
    setSelectedAppointment(undefined);
    setReturnToClientId(navigationState.clientId || null);
    setInitialFormData({
      clientId: navigationState.clientId || '',
      startTime,
      endTime,
      duration
    });
    setIsFormOpen(true);

    navigate(location.pathname, { replace: true, state: null });
  }, [defaultVisitDuration, location.pathname, location.state, navigate]);

  useEffect(() => {
    const unsubscribe = subscribeUserPreferences(() => {
      setWorkingSettings(getCalendarWorkingSettings());
      setAgendaDenseMode(getAgendaDenseMode());
      setDefaultVisitDuration(getDefaultVisitDuration());
    });

    return unsubscribe;
  }, []);

  // Gestionnaires d'événements du calendrier
  const handleDateChange = (date: Date) => {
    setCurrentDate(date);
  };

  const handleViewChange = (view: CalendarView) => {
    setCurrentView(view);
  };

  const handleAppointmentClick = (appointment: CalendarAppointment) => {
    setSelectedAppointment(appointment);
    setInitialFormData(undefined);
    setIsFormOpen(true);
  };

  const handleTimeSlotClick = (startTime: Date, _duration: number) => {
    const defaultDuration = defaultVisitDuration;
    const endTime = new Date(startTime.getTime() + defaultDuration * 60 * 1000);
    
    setSelectedAppointment(undefined);
    setInitialFormData({
      startTime,
      endTime,
      duration: defaultDuration
    });
    setIsFormOpen(true);
  };

  // Gestionnaires du formulaire
  const handleFormSubmit = async (formData: AppointmentFormData) => {
    setIsLoading(true);
    const isNewAppointment = !selectedAppointment;
    try {
      if (selectedAppointment) {
        // Modification
        await ScheduleApiService.updateAppointment(selectedAppointment.id, formData);
      } else {
        // Création
        await ScheduleApiService.createAppointment(formData);
      }
      
      // Recharger les rendez-vous
      await loadAppointments();
      
      // Fermer le formulaire
      handleFormCancel();

      // Retour automatique vers la fiche client si on est arrivé depuis un client
      if (isNewAppointment && returnToClientId) {
        const clientId = returnToClientId;
        setReturnToClientId(null);
        navigate('/clients', { state: { reopenClientId: clientId } });
        return;
      }

      setFeedback({
        type: 'success',
        message: selectedAppointment
          ? 'Rendez-vous mis à jour avec succès.'
          : 'Rendez-vous créé avec succès.'
      });
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setFeedback({
        type: 'error',
        message: getErrorMessage(error, 'La sauvegarde du rendez-vous a échoué.')
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setSelectedAppointment(undefined);
    setInitialFormData(undefined);
    setReturnToClientId(null);
  };

  const handleDeleteAppointment = async (appointmentId: string) => {
    setIsLoading(true);
    try {
      await ScheduleApiService.deleteAppointment(appointmentId);
      await loadAppointments();
      handleFormCancel();
      setFeedback({
        type: 'success',
        message: 'Rendez-vous supprimé avec succès.'
      });
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      setFeedback({
        type: 'error',
        message: getErrorMessage(error, 'La suppression du rendez-vous a échoué.')
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {feedback && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm flex items-start justify-between gap-3 ${
            feedback.type === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          <span>{feedback.message}</span>
          <button
            type="button"
            onClick={() => setFeedback(null)}
            className="font-medium underline underline-offset-2"
          >
            Fermer
          </button>
        </div>
      )}

      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agenda</h1>
          <p className="text-gray-600">Gérez vos rendez-vous et votre planning</p>
          <p className="text-sm text-gray-500 capitalize mt-1">{periodLabel}</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button
            variant="primary"
            onClick={() => {
              setSelectedAppointment(undefined);
              setInitialFormData({
                startTime: new Date(),
                endTime: new Date(Date.now() + defaultVisitDuration * 60 * 1000),
                duration: defaultVisitDuration
              });
              setIsFormOpen(true);
            }}
          >
            Nouveau rendez-vous
          </Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <p className="text-xs uppercase tracking-wide text-slate-500">Total</p>
            <p className="text-xl font-semibold text-slate-900">{appointments.length}</p>
          </div>
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
            <p className="text-xs uppercase tracking-wide text-blue-600">Actifs</p>
            <p className="text-xl font-semibold text-blue-900">
              {statusCounts.scheduled + statusCounts.confirmed + statusCounts.in_progress}
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            <p className="text-xs uppercase tracking-wide text-gray-600">Terminés</p>
            <p className="text-xl font-semibold text-gray-900">{statusCounts.completed}</p>
          </div>
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
            <p className="text-xs uppercase tracking-wide text-red-600">À surveiller</p>
            <p className="text-xl font-semibold text-red-900">{statusCounts.cancelled + statusCounts.no_show}</p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={enableAllStatuses}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              visibleStatuses.length === ALL_STATUS_VALUES.length
                ? 'border-slate-400 bg-slate-100 text-slate-900'
                : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            Tous ({appointments.length})
          </button>

          {STATUS_CONFIG.map((status) => {
            const enabled = visibleStatuses.includes(status.value);
            return (
              <button
                key={status.value}
                type="button"
                onClick={() => toggleStatusFilter(status.value)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  enabled
                    ? status.className
                    : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                }`}
              >
                {status.label} ({statusCounts[status.value]})
              </button>
            );
          })}
        </div>
      </Card>

      {/* Composant Calendar restauré */}
      <Card className="h-[800px] overflow-hidden flex flex-col" padding="none">
        <Calendar
          view={currentView}
          currentDate={currentDate}
          appointments={filteredAppointments}
          workingSettings={workingSettings}
          denseMode={agendaDenseMode}
          onDateChange={handleDateChange}
          onViewChange={handleViewChange}
          onAppointmentClick={handleAppointmentClick}
          onTimeSlotClick={handleTimeSlotClick}
          isLoading={isLoading}
        />
      </Card>

      {/* Formulaire de rendez-vous */}
      <AppointmentForm
        isOpen={isFormOpen}
        appointment={selectedAppointment}
        initialData={initialFormData}
        workingSettings={workingSettings}
        onSubmit={handleFormSubmit}
        onDelete={handleDeleteAppointment}
        onCancel={handleFormCancel}
        isLoading={isLoading}
      />
    </div>
  );
};

export default Schedule;